const DEFAULT_LANG: string = "en";

const LANGS: string[] = ["en", "fr"];

// Will contain the language info (lang_info.en = {token})
const lang_info: any = {};

let current_lang: string = getDefaultLang();
const updateList: any[] = [];

// This is so we can call hOP on non-regular objects like props
const hOP: Function = Object.prototype.hasOwnProperty;

/**
 * The idea behind getting default lang is to look at the browser settings
 * and see if the lang exists in our LANG array, if not, it's possible that
 * the lang was 'en-US' so we want to cut '-US' from it and check again. If
 * we didn't find the accepted lang, we'll default to hard-coded language.
 * 
 * @todo If we store the specified language in local-storage or cookies,
 *             the detection will come here.
 * 
 * @returns accepted language
 */
function getDefaultLang(): string {
    const navig: Navigator = window.navigator;
    let lang: string = navig.language;
    let index: number = LANGS.indexOf(lang);
    const dashIndex: number = lang.indexOf('-');

    if (!~index) {
        if(~dashIndex) {
            lang = lang.substring(0, dashIndex);
            index = LANGS.indexOf(lang);
            if(!~index) {
                lang = DEFAULT_LANG;
            }
        } else {
            lang = DEFAULT_LANG;
        }
    }

    return lang;
}

/**
 * Returns a supported locale from the browser locale settings and the available
 * language LCIDs.
 * 
 * @param availableLcids - Array of available language LCIDs.
 * @param defaultLcid - Default LCID in case the browser default locale is not supported.
 * @returns the locale string of the language.
 */
export function getSupportedLocale(availableLcids: number[], defaultLcid: number): string|null {
    const browserLanguage: string = getDefaultLang();
    const lcidBrowserLang: string|null = Localized.get('lcid-' + browserLanguage);
    
    if (~availableLcids.indexOf(Number(lcidBrowserLang))) {
        return browserLanguage;
    } else {
        return Localized.get(defaultLcid + '-locale');
    }
}

/**
 * Adds data to a string based on raw string and data object. Example:
 *    str = 'Today is [date]'
 *    model = {date: (new Date()).toLocaleDateString()}
 *    result is 'Today is 6/2/2017'
 * 
 * @param str - specifies the raw string (no data)
 * @param model - specifies the data for templating
 * @returns the templated string
 */
function template(str: string, model: Record<string, string>): string {
    for(let name in model) {
        if(hOP.call(model, name)) {
            str = str.replace('[' + name + ']', model[name]);
        }
    }
    return str;
}

/**
 * To change the global current language, set
 * 
 *        Localized.lang = 'es'
 */
export const Localized = {
    /**
     * @returns currently specified language
     */
    get lang(): string {
        return current_lang;
    },

    /**
     * Sets the specified current language and updates the mounted components.
     * @param lang - the specified language
     */
    set lang(lang: string) {
        if (current_lang === lang)
            return;

        Localized.loadLang(lang)
            .then(() => {
                current_lang = lang;
                updateList.forEach(subscriber => subscriber.localizedUpdate ? subscriber.localizedUpdate() : subscriber());
            });
    },

    /**
     * Add a subscriber to an update list
     * @param subscriber - function or an object with a function localizedUpdate
     */
    subscribe(subscriber: any): void {
        updateList.push(subscriber);
    },
    
    /**
     * Remove a subscriber from an update list
     * @param subscriber - function or an object with a function localizedUpdate
     */
    unsubscribe(subscriber: any): void {
        const id: number = updateList.indexOf(subscriber);
        if (~id) updateList.splice(id, 1);
    },
    
    /**
     * @param lang - which language?
     * @returns true if the language was loaded, false otherwise 
     */
    isLangLoaded(lang: string): boolean {
        return lang in lang_info;
    },

    /**
     * Loads a specified language (unless already loaded) and puts it into
     * `lang_info` object.
     * 
     * @param lang - which language to load?
     * @returns a promise that resolves to the language info
     */
    loadLang(lang: string): Promise<Record<string, string>> {
        if (Localized.isLangLoaded(lang)) {
            return Promise.resolve(lang_info[lang]);
        }

        return import("./lang/" + lang + ".json")
            .then((lang_data) => {
                lang_info[lang] = lang_data
                return lang_info[lang];
            });
    },

    /**
     * Gets a translated + templated string
     * 
     * @param token - which token do we need?
     * @param specified_model - model for templating
     * @param specified_lang - which language do we need?
     * @returns the translated string
     */
    get(token: string, specified_model?: Record<string, string>, specified_lang?: string): string {
        const lang: string = typeof specified_lang !== 'undefined' ? specified_lang : current_lang;
        const info = lang_info[lang];

        if (info === undefined) {
            return '';
        }

        const model = typeof specified_model !== 'undefined' ? specified_model : {};
        const str = info[token];
        
        if (str === undefined) {
            return '';
        }
        
        return template(str, model);
    }
}

// This will trigger the language loading
Localized.lang = current_lang;